/**
 * Zoho Sign Environment Configuration Service
 *
 * Provides environment-aware URL configuration for Zoho Sign middleware integration.
 * Automatically detects the current environment and returns appropriate endpoints
 * for health checks, contract sending, and debugging.
 *
 * Supports:
 * - Production environment (lms.der-lagermeister.de)
 * - Development environment (dev.lms.der-lagermeister.de)
 * - Local development (localhost)
 * - Environment variable override support
 *
 * Usage:
 * import zohoConfig from '@/service/zohoConfig'
 * const healthUrl = zohoConfig.healthEndpoint()
 * const sendUrl = zohoConfig.sendTemplateEndpoint()
 */

/**
 * Get Zoho middleware base URL based on current environment
 */
function getZohoMiddlewareUrl() {
  // Environment variable takes priority (allows override)
  if (process.env.VUE_APP_ZOHO_MIDDLEWARE_URL) {
    return process.env.VUE_APP_ZOHO_MIDDLEWARE_URL
  }

  // Auto-detect based on current hostname
  const {hostname} = window.location

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3030'
  }

  // Development environment
  if (hostname.includes('dev.') || hostname.includes('development.')) {
    return 'https://dev.sign.der-lagermeister.de'
  }

  // Production environment (default)
  return 'https://sign.der-lagermeister.de'
}

/**
 * Get current environment name for logging and debugging
 */
function getEnvironmentName() {
  const {hostname} = window.location

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local'
  }

  if (hostname.includes('dev.')) {
    return 'development'
  }

  return 'production'
}

/**
 * Get health check endpoint URL
 */
function healthEndpoint() {
  return `${getZohoMiddlewareUrl()}/health`
}

/**
 * Get template sending endpoint URL
 */
function sendTemplateEndpoint() {
  return `${getZohoMiddlewareUrl()}/api/send-template`
}

/**
 * Get debug connectivity endpoint URL
 */
function debugConnectivityEndpoint() {
  return `${getZohoMiddlewareUrl()}/debug/zoho-connectivity`
}

/**
 * Get document PDF endpoint URL
 * @param {string} documentId - The Zoho Sign document/request ID
 */
function documentPdfEndpoint(documentId) {
  return `${getZohoMiddlewareUrl()}/api/document-pdf/${documentId}`
}

/**
 * Get environment-specific configuration settings
 */
function getEnvironmentConfig() {
  const env = getEnvironmentName()

  const configs = {
    local: {
      timeout: 5000,
      healthCheckInterval: 10000, // Check every 10 seconds in local
      maxRetries: 2,
      enableDetailedLogging: true
    },
    development: {
      timeout: 10000,
      healthCheckInterval: 30000, // Check every 30 seconds
      maxRetries: 3,
      enableDetailedLogging: true
    },
    production: {
      timeout: 15000,
      healthCheckInterval: 60000, // Check every minute
      maxRetries: 3,
      enableDetailedLogging: false
    }
  }

  return configs[env] || configs.production
}

/**
 * Validate current configuration
 */
function validateConfiguration() {
  const baseUrl = getZohoMiddlewareUrl()
  const env = getEnvironmentName()

  const issues = []

  // Check if URL is accessible format
  try {
    // eslint-disable-next-line no-new
    new URL(baseUrl)
  } catch (error) {
    issues.push(`Invalid base URL format: ${baseUrl}`)
  }

  // Environment-specific validation
  if (env === 'local' && !baseUrl.startsWith('http://localhost')) {
    issues.push('Local environment should use http://localhost')
  }

  if (env === 'production' && !baseUrl.startsWith('https://')) {
    issues.push('Production environment should use HTTPS')
  }

  return {
    isValid: issues.length === 0,
    issues,
    configuration: {
      baseUrl,
      environment: env,
      healthEndpoint: healthEndpoint(),
      sendEndpoint: sendTemplateEndpoint(),
      debugEndpoint: debugConnectivityEndpoint()
    }
  }
}

/**
 * Get comprehensive configuration info for debugging
 */
function getConfigurationInfo() {
  const validation = validateConfiguration()
  const envConfig = getEnvironmentConfig()

  return {
    ...validation.configuration,
    environmentSettings: envConfig,
    validation: {
      isValid: validation.isValid,
      issues: validation.issues
    },
    runtime: {
      hostname: window.location.hostname,
      protocol: window.location.protocol,
      environmentVariable: process.env.VUE_APP_ZOHO_MIDDLEWARE_URL || 'not-set',
      timestamp: new Date().toISOString()
    }
  }
}

// Export default object with all functions
export default {
  // Core URL functions
  getZohoMiddlewareUrl,
  healthEndpoint,
  sendTemplateEndpoint,
  debugConnectivityEndpoint,
  documentPdfEndpoint,

  // Environment functions
  getEnvironmentName,
  getEnvironmentConfig,

  // Validation and debugging
  validateConfiguration,
  getConfigurationInfo
}
